import { makeAuthenticatedRequest } from './authService';

/**
 * Garden Service
 * Handles CRUD operations for garden areas and plants
 */

export interface GardenArea {
  id: number;
  user_id: number;
  name: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  color: string;
  year_number?: number | null;
  life_year_label?: string | null;
  created_at: string;
  updated_at: string;
}

export interface GardenPlant {
  id: number;
  area_id: number;
  type: 'flower' | 'tree' | null;
  emoji: string | null;
  position_x: number;
  position_y: number;
  plant_date?: string | null;
  activity_score?: number | null;
  notes?: string | null;
  created_at: string;
}

export interface CreateAreaRequest {
  user_id: number;
  name: string;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  color?: string;
}

export interface UpdateAreaRequest {
  name?: string;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  color?: string;
}

export interface CreatePlantRequest {
  area_id: number;
  type: 'flower' | 'tree';
  emoji: string;
  position_x?: number;
  position_y?: number;
}

export interface UpdatePlantRequest {
  position_x?: number;
  position_y?: number;
  emoji?: string;
  notes?: string | null;
}

export interface GardenAreasResponse {
  success: boolean;
  areas: GardenArea[];
  message?: string;
}

export interface GardenAreaResponse {
  success: boolean;
  area: GardenArea;
  message?: string;
}

export interface GardenPlantsResponse {
  success: boolean;
  plants: GardenPlant[];
  message?: string;
}

export interface GardenResponse {
  success: boolean;
  message?: string;
  id?: number;
}

export interface GeneratePlantsRequest {
  user_id: number;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
}

export interface GeneratePlantsResponse {
  success: boolean;
  message?: string;
  summary?: {
    created: number;
    updated: number;
    skipped: number;
    total: number;
  };
}

export interface UserConfigResponse {
  success: boolean;
  birth_date: string | null;
  message?: string;
}

export interface SetUserConfigRequest {
  user_id: number;
  birth_date: string; // YYYY-MM-DD
}

/**
 * Get all garden areas for a user
 */
export const getUserAreas = async (userId: number): Promise<GardenAreasResponse> => {
  return await makeAuthenticatedRequest(`/garden/areas?user_id=${userId}`, {
    method: 'GET'
  });
};

/**
 * Get a garden area by ID
 */
export const getArea = async (areaId: number): Promise<GardenAreaResponse> => {
  return await makeAuthenticatedRequest(`/garden/areas/${areaId}`, {
    method: 'GET'
  });
};

/**
 * Create a new garden area
 */
export const createArea = async (data: CreateAreaRequest): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest('/garden/areas', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * Update a garden area
 */
export const updateArea = async (areaId: number, data: UpdateAreaRequest): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest(`/garden/areas/${areaId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

/**
 * Delete a garden area
 */
export const deleteArea = async (areaId: number): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest(`/garden/areas/${areaId}`, {
    method: 'DELETE'
  });
};

/**
 * Get all plants for an area
 */
export const getAreaPlants = async (areaId: number): Promise<GardenPlantsResponse> => {
  return await makeAuthenticatedRequest(`/garden/areas/${areaId}/plants`, {
    method: 'GET'
  });
};

/**
 * Get all plants for a user (across all areas)
 */
export const getUserPlants = async (userId: number): Promise<GardenPlantsResponse> => {
  return await makeAuthenticatedRequest(`/garden/plants?user_id=${userId}`, {
    method: 'GET'
  });
};

/**
 * Create a new plant
 */
export const createPlant = async (data: CreatePlantRequest): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest('/garden/plants', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * Update a plant
 */
export const updatePlant = async (plantId: number, data: UpdatePlantRequest): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest(`/garden/plants/${plantId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

/**
 * Delete a plant
 */
export const deletePlant = async (plantId: number): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest(`/garden/plants/${plantId}`, {
    method: 'DELETE'
  });
};

/**
 * Get plant notes
 */
export const getPlantNotes = async (plantId: number): Promise<{ success: boolean; notes: string; message?: string }> => {
  return await makeAuthenticatedRequest(`/garden/plants/${plantId}/notes`, {
    method: 'GET'
  });
};

/**
 * Update plant notes
 */
export const updatePlantNotes = async (plantId: number, notes: string): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest(`/garden/plants/${plantId}/notes`, {
    method: 'PUT',
    body: JSON.stringify({ notes })
  });
};

/**
 * Generate plants for a date range
 */
export const generatePlantsForDateRange = async (data: GeneratePlantsRequest): Promise<GeneratePlantsResponse> => {
  return await makeAuthenticatedRequest('/garden/generate-plants', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * Get user garden configuration (birth date)
 */
export const getUserConfig = async (userId: number): Promise<UserConfigResponse> => {
  return await makeAuthenticatedRequest(`/garden/user-config?user_id=${userId}`, {
    method: 'GET'
  });
};

/**
 * Set user birth date
 */
export const setUserBirthDate = async (data: SetUserConfigRequest): Promise<GardenResponse> => {
  return await makeAuthenticatedRequest('/garden/user-config', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

